import { apiRequest, getAuthToken, API_BASE_URL } from './api';

const ADMIN_BASE = '/api/admin/direct-sold';

const adminRequest = apiRequest;

// ====================================
// Admin Status
// ====================================

/** Check if current user has admin access. Returns true if 200, false if 403/401. */
export async function checkAdminStatus(): Promise<boolean> {
  try {
    await adminRequest(`${ADMIN_BASE}/status`);
    return true;
  } catch {
    return false;
  }
}

// ====================================
// Rate Cards
// ====================================

/** Get the current rate card configuration. */
export async function getRateCards(): Promise<any> {
  return adminRequest(`${ADMIN_BASE}/rate-cards`);
}

/** Update rate card configuration with partial updates. */
export async function updateRateCards(updates: any): Promise<any> {
  return adminRequest(`${ADMIN_BASE}/rate-cards`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

/** Seed rate cards with default configuration. */
export async function seedRateCards(): Promise<any> {
  return adminRequest(`${ADMIN_BASE}/rate-cards/seed`, {
    method: 'POST',
  });
}

// ====================================
// Processing
// ====================================

/** Trigger a processing run. */
export async function triggerProcessing(options: { month?: string; dryRun?: boolean }): Promise<any> {
  return adminRequest(`${ADMIN_BASE}/process`, {
    method: 'POST',
    body: JSON.stringify(options),
  });
}

/** Get list of processing runs. */
export async function getProcessingRuns(limit?: number): Promise<any> {
  const query = limit ? `?limit=${limit}` : '';
  return adminRequest(`${ADMIN_BASE}/runs${query}`);
}

/** Get a specific processing run by ID. */
export async function getProcessingRun(runId: string): Promise<any> {
  return adminRequest(`${ADMIN_BASE}/runs/${runId}`);
}

/** Cancel a stuck or running processing run. */
export async function cancelProcessingRun(runId: string): Promise<any> {
  return adminRequest(`${ADMIN_BASE}/runs/${runId}/cancel`, {
    method: 'POST',
  });
}

// ====================================
// Admin Settings
// ====================================

/** Get admin settings. */
export async function getAdminSettings(): Promise<any> {
  return adminRequest(`${ADMIN_BASE}/settings`);
}

/** Update admin settings with partial updates. */
export async function updateAdminSettings(updates: any): Promise<any> {
  return adminRequest(`${ADMIN_BASE}/settings`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

// ====================================
// Sales Follow-Up Scanner
// ====================================

const SALES_FU_BASE = '/api/admin/sales-follow-up';

/** Get sales follow-up settings. */
export async function getSalesFollowUpSettings(): Promise<any> {
  return adminRequest(`${SALES_FU_BASE}/settings`);
}

/** Update sales follow-up settings. */
export async function updateSalesFollowUpSettings(updates: any): Promise<any> {
  return adminRequest(`${SALES_FU_BASE}/settings`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

/** Seed default sales follow-up settings. */
export async function seedSalesFollowUpSettings(): Promise<any> {
  return adminRequest(`${SALES_FU_BASE}/settings/seed`, {
    method: 'POST',
  });
}

/** Get sales follow-up run history. */
export async function getSalesFollowUpRuns(limit?: number): Promise<any> {
  const query = limit ? `?limit=${limit}` : '';
  return adminRequest(`${SALES_FU_BASE}/runs${query}`);
}

/** Get a single sales follow-up run (includes previewHtml for dry runs). */
export async function getSalesFollowUpRun(runId: string): Promise<any> {
  return adminRequest(`${SALES_FU_BASE}/runs/${encodeURIComponent(runId)}`);
}

/** Trigger a manual sales follow-up scan. Set `opts.dryRun=true` to skip the email send. */
export async function triggerSalesFollowUp(opts: { dryRun?: boolean } = {}): Promise<any> {
  return adminRequest(`${SALES_FU_BASE}/trigger`, {
    method: 'POST',
    body: JSON.stringify({ dryRun: opts.dryRun ?? false }),
  });
}

/** Pause the sales follow-up scanner. */
export async function pauseSalesFollowUp(): Promise<any> {
  return adminRequest(`${SALES_FU_BASE}/pause`, {
    method: 'POST',
  });
}

/** Resume the sales follow-up scanner. */
export async function resumeSalesFollowUp(): Promise<any> {
  return adminRequest(`${SALES_FU_BASE}/resume`, {
    method: 'POST',
  });
}

// ====================================
// Network Performance Report
// ====================================

const NETWORK_REPORT_BASE = '/api/admin/network-report';

/** Get network report settings. */
export async function getNetworkReportSettings(): Promise<any> {
  return adminRequest(`${NETWORK_REPORT_BASE}/settings`);
}

/** Update network report settings. */
export async function updateNetworkReportSettings(updates: any): Promise<any> {
  return adminRequest(`${NETWORK_REPORT_BASE}/settings`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

/** Seed default network report settings. */
export async function seedNetworkReportSettings(): Promise<any> {
  return adminRequest(`${NETWORK_REPORT_BASE}/settings/seed`, {
    method: 'POST',
  });
}

/** Get network report run history. */
export async function getNetworkReportRuns(limit?: number): Promise<any> {
  const query = limit ? `?limit=${limit}` : '';
  return adminRequest(`${NETWORK_REPORT_BASE}/runs${query}`);
}

/** Trigger a manual network report. */
export async function triggerNetworkReport(): Promise<any> {
  return adminRequest(`${NETWORK_REPORT_BASE}/trigger`, {
    method: 'POST',
  });
}

/** Pause the network report scheduler. */
export async function pauseNetworkReport(): Promise<any> {
  return adminRequest(`${NETWORK_REPORT_BASE}/pause`, {
    method: 'POST',
  });
}

/** Resume the network report scheduler. */
export async function resumeNetworkReport(): Promise<any> {
  return adminRequest(`${NETWORK_REPORT_BASE}/resume`, {
    method: 'POST',
  });
}

// ====================================
// Meeting Digest (org-wide singleton)
// ====================================

const MEETING_DIGEST_BASE = '/api/admin/meeting-digest';

/** Get meeting digest settings. */
export async function getMeetingDigestSettings(): Promise<any> {
  return adminRequest(`${MEETING_DIGEST_BASE}/settings`);
}

/** Update meeting digest settings. */
export async function updateMeetingDigestSettings(updates: any): Promise<any> {
  return adminRequest(`${MEETING_DIGEST_BASE}/settings`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

/** Seed default meeting digest settings. */
export async function seedMeetingDigestSettings(): Promise<any> {
  return adminRequest(`${MEETING_DIGEST_BASE}/settings/seed`, {
    method: 'POST',
  });
}

/** Get meeting digest run history. */
export async function getMeetingDigestRuns(limit?: number): Promise<any> {
  const query = limit ? `?limit=${limit}` : '';
  return adminRequest(`${MEETING_DIGEST_BASE}/runs${query}`);
}

/** Trigger a manual meeting digest run. Set `opts.dryRun=true` to skip the email send. */
export async function triggerMeetingDigest(opts: { dryRun?: boolean } = {}): Promise<any> {
  return adminRequest(`${MEETING_DIGEST_BASE}/trigger`, {
    method: 'POST',
    body: JSON.stringify({ dryRun: opts.dryRun ?? false }),
  });
}

/** Pause the meeting digest scheduler. */
export async function pauseMeetingDigest(): Promise<any> {
  return adminRequest(`${MEETING_DIGEST_BASE}/pause`, {
    method: 'POST',
  });
}

/** Resume the meeting digest scheduler. */
export async function resumeMeetingDigest(): Promise<any> {
  return adminRequest(`${MEETING_DIGEST_BASE}/resume`, {
    method: 'POST',
  });
}

// ====================================
// Executive Calendar (multi-task)
// ====================================

const EXECUTIVE_CALENDAR_BASE = '/api/admin/executive-calendar';

export interface ExecutiveCalendarTask {
  id: string;
  name: string;
  executiveEmails: string[];
  recipientEmails: string[];
  enabled: boolean;
  scheduleCron: string;
  scheduleTimezone: string;
  daysAhead: number;
  cloudSchedulerJobName?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

/** List all executive-calendar tasks. */
export async function listExecutiveCalendarTasks(): Promise<{ tasks: ExecutiveCalendarTask[] }> {
  return adminRequest(`${EXECUTIVE_CALENDAR_BASE}/tasks`);
}

/** Seed the two default tasks (Shaun+Fred → Eden, Rik → Tiffany) if none exist. */
export async function seedExecutiveCalendarTasks(): Promise<{ message: string; seeded: ExecutiveCalendarTask[] }> {
  return adminRequest(`${EXECUTIVE_CALENDAR_BASE}/tasks/seed`, { method: 'POST' });
}

/** Create a new executive-calendar task. */
export async function createExecutiveCalendarTask(input: {
  name: string;
  executiveEmails: string[];
  recipientEmails: string[];
  enabled?: boolean;
  scheduleCron?: string;
  scheduleTimezone?: string;
  daysAhead?: number;
}): Promise<{ task: ExecutiveCalendarTask }> {
  return adminRequest(`${EXECUTIVE_CALENDAR_BASE}/tasks`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Update an existing executive-calendar task. */
export async function updateExecutiveCalendarTask(
  taskId: string,
  updates: Partial<{
    name: string;
    executiveEmails: string[];
    recipientEmails: string[];
    enabled: boolean;
    scheduleCron: string;
    scheduleTimezone: string;
    daysAhead: number;
  }>
): Promise<{ task: ExecutiveCalendarTask }> {
  return adminRequest(`${EXECUTIVE_CALENDAR_BASE}/tasks/${taskId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

/** Delete an executive-calendar task. */
export async function deleteExecutiveCalendarTask(taskId: string): Promise<any> {
  return adminRequest(`${EXECUTIVE_CALENDAR_BASE}/tasks/${taskId}`, {
    method: 'DELETE',
  });
}

/** Manually trigger an executive-calendar task. Pass dryRun=true to skip the email send. */
export async function triggerExecutiveCalendarTask(
  taskId: string,
  opts: { dryRun?: boolean } = {}
): Promise<any> {
  return adminRequest(`${EXECUTIVE_CALENDAR_BASE}/tasks/${taskId}/trigger`, {
    method: 'POST',
    body: JSON.stringify({ dryRun: opts.dryRun ?? false }),
  });
}

/** Pause an executive-calendar task. */
export async function pauseExecutiveCalendarTask(taskId: string): Promise<any> {
  return adminRequest(`${EXECUTIVE_CALENDAR_BASE}/tasks/${taskId}/pause`, {
    method: 'POST',
  });
}

/** Resume an executive-calendar task. */
export async function resumeExecutiveCalendarTask(taskId: string): Promise<any> {
  return adminRequest(`${EXECUTIVE_CALENDAR_BASE}/tasks/${taskId}/resume`, {
    method: 'POST',
  });
}

/** Get run history for an executive-calendar task. */
export async function getExecutiveCalendarRuns(taskId: string, limit?: number): Promise<{ runs: any[] }> {
  const query = limit ? `?limit=${limit}` : '';
  return adminRequest(`${EXECUTIVE_CALENDAR_BASE}/tasks/${taskId}/runs${query}`);
}

// ====================================
// Direct Sold Pause/Resume
// ====================================

/** Pause the direct sold scheduler. */
export async function pauseDirectSold(): Promise<any> {
  return adminRequest(`${ADMIN_BASE}/pause`, {
    method: 'POST',
  });
}

/** Resume the direct sold scheduler. */
export async function resumeDirectSold(): Promise<any> {
  return adminRequest(`${ADMIN_BASE}/resume`, {
    method: 'POST',
  });
}

// ====================================
// RFP Inbox Scanner
// ====================================

const RFP_BASE = '/api/admin/rfp';

/** Get RFP scanner settings. */
export async function getRfpSettings(): Promise<any> {
  return adminRequest(`${RFP_BASE}/settings`);
}

/** Update RFP scanner settings. */
export async function updateRfpSettings(updates: any): Promise<any> {
  return adminRequest(`${RFP_BASE}/settings`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

/** Seed default RFP settings. */
export async function seedRfpSettings(): Promise<any> {
  return adminRequest(`${RFP_BASE}/settings/seed`, {
    method: 'POST',
  });
}

/** Get RFP processing run history. */
export async function getRfpRuns(limit?: number): Promise<any> {
  const query = limit ? `?limit=${limit}` : '';
  return adminRequest(`${RFP_BASE}/runs${query}`);
}

/** Trigger a manual RFP inbox scan. Optional searchTimeWindowHours for one-time wider scans. */
export async function triggerRfpProcessing(searchTimeWindowHours?: number): Promise<any> {
  return adminRequest(`${RFP_BASE}/trigger`, {
    method: 'POST',
    body: searchTimeWindowHours ? JSON.stringify({ searchTimeWindowHours }) : undefined,
  });
}

/** Pause the RFP inbox scanner. */
export async function pauseRfpProcessing(): Promise<any> {
  return adminRequest(`${RFP_BASE}/pause`, {
    method: 'POST',
  });
}

/** Resume the RFP inbox scanner. */
export async function resumeRfpProcessing(): Promise<any> {
  return adminRequest(`${RFP_BASE}/resume`, {
    method: 'POST',
  });
}

// ====================================
// IO Approval Forwarder (Admin)
// ====================================

const IO_APPROVAL_BASE = '/api/admin/io-approval';

/** Get IO Approval Forwarder settings. */
export async function getIoApprovalSettings(): Promise<any> {
  return adminRequest(`${IO_APPROVAL_BASE}/settings`);
}

/** Update IO Approval Forwarder settings. */
export async function updateIoApprovalSettings(updates: any): Promise<any> {
  return adminRequest(`${IO_APPROVAL_BASE}/settings`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

/** Seed default IO Approval Forwarder settings. */
export async function seedIoApprovalSettings(): Promise<any> {
  return adminRequest(`${IO_APPROVAL_BASE}/settings/seed`, {
    method: 'POST',
  });
}

/** Get IO Approval Forwarder run history. */
export async function getIoApprovalRuns(limit?: number): Promise<any> {
  const query = limit ? `?limit=${limit}` : '';
  return adminRequest(`${IO_APPROVAL_BASE}/runs${query}`);
}

/** Trigger a manual IO Approval inbox scan. Optional lookbackHours for one-time wider scans. */
export async function triggerIoApprovalProcessing(lookbackHours?: number): Promise<any> {
  return adminRequest(`${IO_APPROVAL_BASE}/trigger`, {
    method: 'POST',
    body: lookbackHours ? JSON.stringify({ lookbackHours }) : undefined,
  });
}

/** Pause the IO Approval Forwarder. */
export async function pauseIoApprovalProcessing(): Promise<any> {
  return adminRequest(`${IO_APPROVAL_BASE}/pause`, {
    method: 'POST',
  });
}

/** Resume the IO Approval Forwarder. */
export async function resumeIoApprovalProcessing(): Promise<any> {
  return adminRequest(`${IO_APPROVAL_BASE}/resume`, {
    method: 'POST',
  });
}

// ====================================
// Channel Mapping (Firestore-backed)
// ====================================

/** Get the persistent channel mapping document from Firestore. */
export async function getChannelMapping(): Promise<any> {
  return adminRequest(`${ADMIN_BASE}/channel-mapping`);
}

/** Refresh channel mapping from ChannelMeter + Airtable. */
export async function refreshChannelMapping(): Promise<any> {
  return adminRequest(`${ADMIN_BASE}/channel-mapping/refresh`, { method: 'POST' });
}

/** Seed channel mapping from legacy hardcoded data (one-time). */
export async function seedChannelMapping(): Promise<any> {
  return adminRequest(`${ADMIN_BASE}/channel-mapping/seed`, { method: 'POST' });
}

/** Add a new channel mapping entry. */
export async function addChannelMappingEntry(entry: {
  channelName: string;
  youtubeChannelId: string;
  channelUrl?: string;
  driveFolderId?: string;
}): Promise<any> {
  return adminRequest(`${ADMIN_BASE}/channel-mapping`, {
    method: 'POST',
    body: JSON.stringify(entry),
  });
}

/** Update an existing channel mapping entry. */
export async function updateChannelMappingEntry(
  key: string,
  update: { youtubeChannelId?: string; driveFolderId?: string | null }
): Promise<any> {
  return adminRequest(`${ADMIN_BASE}/channel-mapping/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: JSON.stringify(update),
  });
}

/** Delete a channel mapping entry. */
export async function deleteChannelMappingEntry(key: string): Promise<any> {
  return adminRequest(`${ADMIN_BASE}/channel-mapping/${encodeURIComponent(key)}`, {
    method: 'DELETE',
  });
}

/** Reset a manually edited channel entry so next refresh can overwrite. */
export async function resetChannelMappingEntry(key: string): Promise<any> {
  return adminRequest(`${ADMIN_BASE}/channel-mapping/${encodeURIComponent(key)}/reset`, {
    method: 'POST',
  });
}

/** Rename a channel mapping entry (changes both key and display name). */
export async function renameChannelMappingEntry(
  key: string,
  channelName: string
): Promise<any> {
  return adminRequest(`${ADMIN_BASE}/channel-mapping/${encodeURIComponent(key)}/rename`, {
    method: 'POST',
    body: JSON.stringify({ channelName }),
  });
}

/** Scan Google Drive folders and auto-match to channel mapping entries. */
export async function scanChannelFolders(): Promise<any> {
  return adminRequest(`${ADMIN_BASE}/channel-mapping/scan-folders`, { method: 'POST' });
}

// ====================================
// ChannelMeter / Financials
// ====================================

const CHANNEL_METER_BASE = '/api/admin/channel-meter';

/** Get aggregated CSV-based financials for a report period. */
/**
 * Financials for one ChannelMeter period. The server's default basis is
 * 'corrected' (ChannelMeter + applied Earnings Corrections); 'paid' is exactly
 * what ChannelMeter paid.
 */
export async function getChannelMeterFinancials(options?: {
  month?: number;
  refresh?: boolean;
  basis?: 'paid' | 'corrected';
}): Promise<any> {
  const params = new URLSearchParams();
  if (options?.month != null) params.set('month', String(options.month));
  if (options?.refresh) params.set('refresh', 'true');
  if (options?.basis === 'paid') params.set('basis', 'paid');
  const qs = params.toString();
  return adminRequest(`${CHANNEL_METER_BASE}/financials${qs ? `?${qs}` : ''}`);
}

/** Start a detached YouTube ad-performance capture (true AdSense ad impressions). */
export async function captureYtAdPerformance(body: {
  month?: string;
  months?: string[];
  all?: boolean;
}): Promise<any> {
  return adminRequest(`${CHANNEL_METER_BASE}/yt-ad-performance/capture`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Poll a YT ad-performance capture (per-month summary) or list all captured months. */
export async function getYtAdPerformanceStatus(month?: string): Promise<any> {
  const qs = month ? `?month=${encodeURIComponent(month)}` : '';
  return adminRequest(`${CHANNEL_METER_BASE}/yt-ad-performance/status${qs}`);
}

/** Get paginated ChannelMeter users with latest payouts. */
export async function getChannelMeterUsers(options?: {
  query?: string;
  limit?: number;
  skip?: number;
}): Promise<any> {
  const params = new URLSearchParams();
  if (options?.query) params.set('query', options.query);
  if (options?.limit != null) params.set('limit', String(options.limit));
  if (options?.skip != null) params.set('skip', String(options.skip));
  const qs = params.toString();
  return adminRequest(`${CHANNEL_METER_BASE}/users${qs ? `?${qs}` : ''}`);
}

/** Get a single ChannelMeter user with full balance history. */
export async function getChannelMeterUser(userId: string): Promise<any> {
  return adminRequest(`${CHANNEL_METER_BASE}/users/${userId}`);
}

/** Get per-user payment breakdown for a specific report. */
export async function getChannelMeterReportPayments(
  reportId: string,
  options?: { includeChannels?: boolean }
): Promise<any> {
  const qs = options?.includeChannels ? '?includeChannels=true' : '';
  return adminRequest(`${CHANNEL_METER_BASE}/reports/${reportId}/payments${qs}`);
}

// ====================================
// Income Deck feature (historical backfill + Gamma deck generation)
// ====================================

export interface KnownChannel {
  channelId: string;
  channelName: string;
  channelUrl?: string;
  monthCount: number;
  hasDirectSold: boolean;
  hasAdSense: boolean;
}

export interface ChannelEarningsSeriesItem {
  channelId: string;
  channelName: string;
  series: Array<{
    month: string;
    directSoldUsd: number;
    adsenseUsd: number;
    totalUsd: number;
    dataSource?: 'cm' | 'sheet' | 'mixed';
  }>;
  totals: {
    directSoldUsd: number;
    adsenseUsd: number;
    totalUsd: number;
  };
}

export interface EarningsSeriesResponse {
  fromMonth: string;
  toMonth: string;
  channels: ChannelEarningsSeriesItem[];
  portfolio: {
    directSoldUsd: number;
    adsenseUsd: number;
    totalUsd: number;
    monthsCovered: number;
  };
}

/** GET /known-channels — list of all channels seen across cmFinancialPeriods. */
export async function getKnownChannels(): Promise<{ channels: KnownChannel[] }> {
  return adminRequest(`${CHANNEL_METER_BASE}/known-channels`);
}

/** GET /earnings-series — per-channel monthly time-series within a date range. */
export async function getEarningsSeries(args: {
  channelIds: string[];
  from: string;
  to: string;
}): Promise<EarningsSeriesResponse> {
  const params = new URLSearchParams({
    channelIds: args.channelIds.join(','),
    from: args.from,
    to: args.to,
  });
  return adminRequest(`${CHANNEL_METER_BASE}/earnings-series?${params.toString()}`);
}

/** Open an authenticated POST + SSE stream. Calls onEvent for each parsed `data: {…}` payload. */
async function streamAdminSse(
  endpoint: string,
  body: any,
  onEvent: (payload: any) => void,
): Promise<void> {
  const { auth } = await import('../config/firebase');
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken(false);

  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  const response = await fetch(`${apiBase}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Request failed (${response.status})`);
  }
  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response body');
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split('\n\n');
    buffer = chunks.pop() || '';
    for (const chunk of chunks) {
      const trimmed = chunk.trim();
      if (!trimmed) continue;
      for (const line of trimmed.split('\n')) {
        if (line.startsWith('data: ')) {
          try {
            onEvent(JSON.parse(line.substring(6)));
          } catch (err) {
            console.error('[adminApi SSE] Failed to parse line:', line, err);
          }
        }
      }
    }
  }
}

/** POST /backfill-earnings-history — streams progress events. */
export async function backfillEarningsHistory(
  args: { mode: 'full' | 'topOff'; monthsBack?: number; dryRun?: boolean; onlyMonth?: string },
  onEvent: (payload: any) => void,
): Promise<void> {
  await streamAdminSse(`${CHANNEL_METER_BASE}/backfill-earnings-history`, args, onEvent);
}

/** POST /income-deck — streams Gamma progress events, ends with `complete` or `error`. */
export async function generateIncomeDeck(
  args: { channelIds: string[]; from: string; to: string; themeName?: string; exportAs?: 'pdf' | 'pptx' | 'png' },
  onEvent: (payload: any) => void,
): Promise<void> {
  await streamAdminSse(`${CHANNEL_METER_BASE}/income-deck`, args, onEvent);
}

/** POST /channel-report — generate a client-facing PDF earnings report (Gamma document). Streams progress. */
export async function generateChannelReport(
  args: { channelIds: string[]; from: string; to: string; scopeLabel?: string; themeName?: string; exportAs?: 'pdf' | 'pptx' },
  onEvent: (payload: any) => void,
): Promise<void> {
  await streamAdminSse(`${CHANNEL_METER_BASE}/channel-report`, args, onEvent);
}

// ====================================
// Login Activity
// ====================================

const LOGIN_ACTIVITY_BASE = '/api/admin/login-activity';

export interface LoginEvent {
  id: string;
  uid: string;
  email: string;
  displayName: string | null;
  organization: 'culturegenesis' | 'partner' | 'external';
  signInAt: string;       // ISO
  signInAtMillis: number; // for cursor
  userAgent: string | null;
}

export interface LoginActivityPage {
  events: LoginEvent[];
  nextCursor: number | null;
}

export async function getLoginActivity(opts: {
  limit?: number;
  before?: number;
  search?: string;
} = {}): Promise<LoginActivityPage> {
  const params = new URLSearchParams();
  if (opts.limit != null) params.set('limit', String(opts.limit));
  if (opts.before != null) params.set('before', String(opts.before));
  if (opts.search) params.set('search', opts.search);
  const qs = params.toString();
  return adminRequest(`${LOGIN_ACTIVITY_BASE}${qs ? `?${qs}` : ''}`);
}

export type ActivitySurface = 'web' | 'connector';

/** One sitting: a web session or a Claude connector session (backend activitySessionService). */
export interface ActivitySession {
  id: string;
  uid: string;
  email: string;
  displayName: string | null;
  organization: 'culturegenesis' | 'partner' | 'external';
  surface: ActivitySurface;
  startedAt: string; // ISO
  startedAtMillis: number; // for cursor
  lastActiveAt: string; // ISO
  lastActiveAtMillis: number;
  /** Web: the sign-in this session began with, if it began with one. */
  signInAt: string | null;
  /** Web: dashboard ids (config/dashboards.ts) opened, most-opened first. */
  dashboards: Array<{ id: string; opens: number }>;
  /** Connector: the connection and the Claude client the calls came from. */
  connector: { gid: string; clientId: string; client: string } | null;
  /** Connector: tools called, most-called first. `errors` = calls that failed. */
  tools: Array<{ name: string; title: string; calls: number; errors: number }>;
  toolCalls: number;
  toolErrors: number;
}

export interface ActivitySessionsPage {
  sessions: ActivitySession[];
  nextCursor: number | null;
}

export async function getActivitySessions(opts: {
  limit?: number;
  before?: number;
  search?: string;
  surface?: ActivitySurface;
} = {}): Promise<ActivitySessionsPage> {
  const params = new URLSearchParams();
  if (opts.limit != null) params.set('limit', String(opts.limit));
  if (opts.before != null) params.set('before', String(opts.before));
  if (opts.search) params.set('search', opts.search);
  if (opts.surface) params.set('surface', opts.surface);
  const qs = params.toString();
  return adminRequest(`${LOGIN_ACTIVITY_BASE}/sessions${qs ? `?${qs}` : ''}`);
}

// ====================================
// Pipeline
// ====================================

export interface PipelineStage {
  status: 'pending' | 'running' | 'completed' | 'failed' | 'confirmed' | 'skipped';
  completedAt?: string;
  confirmedBy?: string;
  confirmedAt?: string;
  notes?: string;
  error?: string;
  runId?: string;
  /** When the stage last transitioned to 'running' */
  startedAt?: string;
  result?: any;
  /** Payout targets currently regenerating (partial payoutSheets re-run) */
  activeTargets?: string[];
}

export interface PipelineRun {
  id: string;
  month: string;
  monthDisplay: string;
  dryRun?: boolean;
  /**
   * AUTOMATIC mode: the server auto-runs validation after DS processing and payout
   * sheets after the lock confirm. ChannelMeter steps always wait for a human.
   */
  autoAdvance?: boolean;
  stages: {
    /** GAM CSV upload precondition — absent on pipelines created before the upload-driven flow */
    reportUpload?: PipelineStage;
    dsProcessing: PipelineStage;
    validation: PipelineStage;
    cmUpload: PipelineStage;
    cmLock: PipelineStage;
    payoutSheets: PipelineStage;
  };
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}

export interface ValidationCheck {
  name: string;
  status: 'passed' | 'warning' | 'blocker';
  message: string;
  details?: any;
}

export interface ValidationResult {
  runId: string;
  month: string;
  checks: ValidationCheck[];
  summary: { passed: number; warnings: number; blockers: number };
  validatedAt: string;
}

/** Get the current (most recent) pipeline */
export async function getPipelineStatus(month?: string): Promise<{ pipeline: PipelineRun | null }> {
  if (month) {
    return adminRequest(`${ADMIN_BASE}/pipeline/${month}`);
  }
  return adminRequest(`${ADMIN_BASE}/pipeline/current`);
}

/** Start a new pipeline for the given month */
export async function startPipeline(
  month?: string,
  dryRun?: boolean,
  autoAdvance?: boolean
): Promise<{ pipeline: PipelineRun; message: string }> {
  return adminRequest(`${ADMIN_BASE}/pipeline/start`, {
    method: 'POST',
    body: JSON.stringify({ month, dryRun, autoAdvance }),
  });
}

/** Flip a live pipeline between Automatic and Manual mode. */
export async function setPipelineMode(
  month: string,
  autoAdvance: boolean
): Promise<{ pipeline: PipelineRun; message: string }> {
  return adminRequest(`${ADMIN_BASE}/pipeline/${month}/mode`, {
    method: 'POST',
    body: JSON.stringify({ autoAdvance }),
  });
}

/** Recent pipelines, newest first (history strip). */
export async function getPipelineHistory(limit = 12): Promise<{ pipelines: PipelineRun[] }> {
  return adminRequest(`${ADMIN_BASE}/pipeline/history?limit=${limit}`);
}

/** Cancel DS processing for a pipeline */
export async function cancelPipelineProcessing(month: string): Promise<{ pipeline: PipelineRun; message: string }> {
  return adminRequest(`${ADMIN_BASE}/pipeline/${month}/cancel`, {
    method: 'POST',
  });
}

/** Discard a dry-run pipeline entirely (deletes the doc) */
export async function discardPipeline(month: string): Promise<{ message: string }> {
  return adminRequest(`${ADMIN_BASE}/pipeline/${month}/discard`, {
    method: 'POST',
  });
}

/** Confirm CM upload completed */
export async function confirmCmUpload(month: string, notes?: string): Promise<{ pipeline: PipelineRun; message: string }> {
  return adminRequest(`${ADMIN_BASE}/pipeline/${month}/confirm-upload`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  });
}

/** Confirm payments locked in CM */
export async function confirmCmLock(month: string): Promise<{ pipeline: PipelineRun; message: string }> {
  return adminRequest(`${ADMIN_BASE}/pipeline/${month}/confirm-lock`, {
    method: 'POST',
  });
}

/**
 * One re-runnable payout output. Result keys on the payoutSheets stage:
 * adSense→adSense, directSold→directSold, trackerSummaries→channelSheetSummary,
 * wasserman→wassermanSheet, team→teamSheet, erica→ericaSheet, skara→skaraSheet,
 * nightco→nightcoSheet.
 */
export type PayoutTarget =
  | 'adSense'
  | 'directSold'
  | 'trackerSummaries'
  | 'wasserman'
  | 'team'
  | 'erica'
  | 'skara'
  | 'nightco';

/**
 * Trigger payout sheet generation. With `targets`, regenerates only those
 * outputs (per-key merged into the stage result); without, the full run.
 */
export async function triggerPipelinePayouts(
  month: string,
  targets?: PayoutTarget[]
): Promise<{ pipeline: PipelineRun; message: string }> {
  return adminRequest(`${ADMIN_BASE}/pipeline/${month}/generate-payouts`, {
    method: 'POST',
    ...(targets?.length ? { body: JSON.stringify({ targets }) } : {}),
  });
}

// ====================================
// GAM Report Upload (manual monthly CSVs — one or more reports per month)
// ====================================

/** GAM's metadata block above the CSV header ("Report name", "Filters", …). */
export interface GamReportPreamble {
  reportName: string | null;
  reportResultId: string | null;
  currency: string | null;
  generatedAt: string | null;
  dateRangeText: string | null;
  timeZone: string | null;
  filtersText: string | null;
}

/** One uploaded GAM report CSV of a month's report set. */
export interface GamReportSourceMeta {
  sourceId: string;
  fileName: string;
  contentHash: string;
  byteSize: number;
  preamble: GamReportPreamble;
  detectedMonth: string | null;
  monthOverridden: boolean;
  rowCount: number;
  skippedEmptyChannelRows: number;
  channelCount: number;
  totalImpressions: number;
  totalRevenue: number;
  parseWarnings: string[];
  uploadedBy: string;
  uploadedAt: string;
}

/**
 * A month's GAM report set. The top-level figures describe the MERGED dataset
 * DS processing reads: fileName joins the files with " + ", and
 * uploadedAt/uploadedBy are the last change. `sources` lists each report.
 */
export interface GamReportUploadMeta {
  month: string;               // effective YYYY-MM
  monthDisplay: string;
  detectedMonth: string | null;
  monthOverridden: boolean;
  fileName: string;
  dateRangeText: string | null;
  uploadedAt: string;
  uploadedBy: string;
  rowCount: number;
  skippedEmptyChannelRows: number;
  channelCount: number;
  totalImpressions: number;
  totalRevenue: number;
  parseWarnings: string[];
  contentHash?: string;
  /** Every report in the set (one for single-report months) */
  sources?: GamReportSourceMeta[];
  /** Bumped on every change — sent back as baseRevision so concurrent edits 409 */
  revision?: number;
}

export interface GamReportUploadResult {
  upload: GamReportUploadMeta;
  replacedExisting: boolean;
  addedSourceIds?: string[];
  removedSourceIds?: string[];
}

/** add = merge with the month's reports · replace = swap out replaceSourceIds · replace-all = start over */
export type GamReportSetMode = 'add' | 'replace' | 'replace-all';

/** Two reports that share ad rows (the 422 `overlap` refusal). */
export interface GamReportOverlap {
  a: { sourceId: string; fileName: string; isNew: boolean };
  b: { sourceId: string; fileName: string; isNew: boolean };
  comparedBy: 'ids' | 'names';
  /** Rows of `b` that repeat a row of `a` */
  rowCount: number;
  impressions: number;
  revenue: number;
  channelCount: number;
  channels: string[];
  sampleRows: Array<{
    channel: string;
    advertiser: string;
    lineItem: string;
    creative: string;
    creativeSize: string;
    impressions: number;
    revenue: number;
  }>;
  /** The overlap covers at least half of the smaller report — the same report exported twice */
  likelyReexport: boolean;
}

/** A refused report-set change: the server's status, machine `code` and `details`. */
export class GamReportUploadError extends Error {
  status: number;
  code?: string;
  details?: any;

  constructor(message: string, status: number, code?: string, details?: any) {
    super(message);
    this.name = 'GamReportUploadError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** The overlapping report pairs of a 422 `overlap` (empty otherwise). */
  get overlaps(): GamReportOverlap[] {
    return this.code === 'overlap' && Array.isArray(this.details?.overlaps) ? this.details.overlaps : [];
  }
}

/** The 409 guard: the month's pipeline already progressed, so the change needs `force`. */
export class GamUploadRequiresForceError extends GamReportUploadError {
  requiresForce = true as const;
}

async function reportUploadFailure(res: Response, fallback: string): Promise<GamReportUploadError> {
  const body = await res.json().catch(() => ({ error: fallback }));
  const message = body?.error || `${fallback} (${res.status})`;
  if (res.status === 409 && body?.requiresForce) {
    return new GamUploadRequiresForceError(message, res.status, body.code || 'requires_force', body.details);
  }
  return new GamReportUploadError(message, res.status, body?.code, body?.details);
}

export interface GamReportChangeOptions {
  mode?: GamReportSetMode;
  /** For mode 'replace': the reports the new files swap out */
  replaceSourceIds?: string[];
  monthOverride?: string;
  force?: boolean;
  /** The set revision the UI showed (0 = nothing uploaded); omitted = don't check */
  baseRevision?: number;
}

/**
 * Upload GAM Direct Sold CSVs into their month's report set (multipart, raw
 * fetch for the boundary). The server merges them with the month's other
 * reports into the one dataset processing reads.
 */
export async function uploadGamReports(
  files: File[],
  opts: GamReportChangeOptions = {}
): Promise<GamReportUploadResult> {
  const token = await getAuthToken();
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  form.append('mode', opts.mode ?? 'add');
  if (opts.replaceSourceIds?.length) form.append('replaceSourceIds', JSON.stringify(opts.replaceSourceIds));
  if (opts.monthOverride) form.append('monthOverride', opts.monthOverride);
  if (opts.force) form.append('force', 'true');
  if (opts.baseRevision !== undefined) form.append('baseRevision', String(opts.baseRevision));
  const res = await fetch(`${API_BASE_URL}${ADMIN_BASE}/report-upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` }, // no Content-Type — browser sets the multipart boundary
    body: form,
  });
  if (!res.ok) throw await reportUploadFailure(res, 'Upload failed');
  return res.json();
}

/** Remove one report from a month's set (the month's only report can't be removed). */
export async function removeGamReportSource(
  month: string,
  sourceId: string,
  opts: { force?: boolean; baseRevision?: number } = {}
): Promise<GamReportUploadResult> {
  const token = await getAuthToken();
  const query = new URLSearchParams();
  if (opts.force) query.set('force', 'true');
  if (opts.baseRevision !== undefined) query.set('baseRevision', String(opts.baseRevision));
  const qs = query.toString();
  const res = await fetch(
    `${API_BASE_URL}${ADMIN_BASE}/report-upload/${encodeURIComponent(month)}/sources/${encodeURIComponent(sourceId)}${qs ? `?${qs}` : ''}`,
    { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) throw await reportUploadFailure(res, 'Could not remove the report');
  return res.json();
}

/**
 * The month's report set ({ upload: null } when nothing is uploaded yet) and
 * how many GAM reports the month is expected to have.
 */
export async function getGamReportUpload(
  month: string
): Promise<{ upload: GamReportUploadMeta | null; expectedReports?: number }> {
  return adminRequest(`${ADMIN_BASE}/report-upload/${month}`);
}

/** Recent uploads, newest first. */
export async function listGamReportUploads(limit = 12): Promise<{ uploads: GamReportUploadMeta[] }> {
  return adminRequest(`${ADMIN_BASE}/report-uploads?limit=${limit}`);
}

export type PipelineRerunStep = 'master-summary' | 'tracker-distribution';

/** Re-run a stage-1 sub-step (master+summary regen / tracker re-distribution). 202-style. */
export async function rerunPipelineStep(
  month: string,
  step: PipelineRerunStep,
  limitToChannel?: string
): Promise<{ runId: string; message: string }> {
  return adminRequest(`${ADMIN_BASE}/pipeline/${month}/rerun/${step}`, {
    method: 'POST',
    ...(limitToChannel ? { body: JSON.stringify({ limitToChannel }) } : {}),
  });
}

/** Send the monthly "upload the GAM report" reminder email now (test send). */
export async function sendDirectSoldReminderTest(month?: string): Promise<{ action: string; month: string; message: string }> {
  return adminRequest(`${ADMIN_BASE}/reminder/test`, {
    method: 'POST',
    ...(month ? { body: JSON.stringify({ month }) } : {}),
  });
}

/** Get validation results for a pipeline month */
export async function getValidationResult(month: string): Promise<{ validation: ValidationResult | null }> {
  return adminRequest(`${ADMIN_BASE}/pipeline/${month}/validation`);
}

/** Run validation for a pipeline month */
export async function runValidation(month: string): Promise<{ validation: ValidationResult }> {
  return adminRequest(`${ADMIN_BASE}/pipeline/${month}/validation`, {
    method: 'POST',
  });
}

// ====================================
// Direct Sold Ad Intelligence
// ====================================

const DIRECT_SOLD_ADS_BASE = '/api/admin/direct-sold-ads';

export type DsAdFormat =
  | 'skippable-video'
  | 'non-skippable-15s'
  | 'non-skippable-30s'
  | 'bumper-6s'
  | 'other';

export interface DsAdRecord {
  id: string;
  month: string;
  channelId: string;
  channelName: string;
  channelResolved: boolean;
  cms: 'culture-genesis' | 'cg-affiliate-2' | 'cg-3' | 'all-def' | 'cg-4' | 'unknown';
  advertiser: string;
  advertiserId: string;
  creativeBrand: string;
  creativeId: string;
  lineItemName: string;
  lineItemId: string;
  creativeSize: string;
  adFormat: DsAdFormat;
  impressions: number;
  clicks: number;
  mediaSalesRevenueUsd: number;
  avgEcpmUsd: number;
  channelEarningsUsd?: number;
  gamOrderId?: string;
  campaignName?: string;
  gamMatchConfidence?: 'high' | 'medium' | 'low';
  source: string;
  trackerSheetTab?: string;
}

export type DsAdFormatMix = Record<DsAdFormat, number>;

export interface DsAdvertiserRollup {
  advertiser: string;
  advertiserId: string;
  channelCount: number;
  recordCount: number;
  impressions: number;
  clicks: number;
  revenueUsd: number;
  formatMix: DsAdFormatMix;
}

export interface DsChannelAdRollup {
  channelId: string;
  channelName: string;
  advertiserCount: number;
  recordCount: number;
  impressions: number;
  revenueUsd: number;
}

export interface DsCampaignRollup {
  gamOrderId: string;
  campaignName?: string;
  channelCount: number;
  cmsList: string[];
  impressions: number;
  recordCount: number;
}

export interface DsAdMonthlyRollup {
  month: string;
  totals: {
    records: number;
    impressions: number;
    clicks: number;
    revenueUsd: number;
    advertiserCount: number;
    channelCount: number;
    campaignCount: number;
    unresolvedChannels: number;
  };
  formatMix: DsAdFormatMix;
  advertisers: DsAdvertiserRollup[];
  channels: DsChannelAdRollup[];
  campaigns: DsCampaignRollup[];
  truncated?: boolean;
  builtAt: string;
}

export interface DsAdStatus {
  canView: boolean;
  canManage: boolean;
  backfillInFlight: boolean;
  enrichmentInFlight: boolean;
  youtubeCaptureInFlight?: boolean;
}

/** Capabilities + whether a backfill/enrichment job is running. */
export async function getDirectSoldAdsStatus(): Promise<DsAdStatus> {
  return adminRequest(`${DIRECT_SOLD_ADS_BASE}/status`);
}

/** Months that have a computed rollup (newest first). */
export async function getDirectSoldAdsMonths(): Promise<{ months: string[] }> {
  return adminRequest(`${DIRECT_SOLD_ADS_BASE}/months`);
}

/** Full precomputed rollup for a month. */
export async function getDirectSoldAdsRollup(month: string): Promise<{ month: string; rollup: DsAdMonthlyRollup | null }> {
  return adminRequest(`${DIRECT_SOLD_ADS_BASE}/rollup?month=${encodeURIComponent(month)}`);
}

/** Records for a single channel in a month (the Advertiser → Line Item drill-down). */
export async function getDirectSoldAdsChannelMonth(month: string, channelId: string): Promise<{ records: DsAdRecord[]; hasMore: boolean }> {
  const params = new URLSearchParams({ month, channelId });
  return adminRequest(`${DIRECT_SOLD_ADS_BASE}/records?${params.toString()}`);
}

/** Filtered, paginated record query. */
export async function getDirectSoldAdsRecords(options: {
  month?: string;
  channelId?: string;
  advertiser?: string;
  adFormat?: DsAdFormat;
  gamOrderId?: string;
  cursor?: string;
  limit?: number;
}): Promise<{ records: DsAdRecord[]; hasMore: boolean; nextCursor?: string }> {
  const params = new URLSearchParams();
  if (options.month) params.set('month', options.month);
  if (options.channelId) params.set('channelId', options.channelId);
  if (options.advertiser) params.set('advertiser', options.advertiser);
  if (options.adFormat) params.set('adFormat', options.adFormat);
  if (options.gamOrderId) params.set('gamOrderId', options.gamOrderId);
  if (options.cursor) params.set('cursor', options.cursor);
  if (options.limit != null) params.set('limit', String(options.limit));
  const qs = params.toString();
  return adminRequest(`${DIRECT_SOLD_ADS_BASE}/records${qs ? `?${qs}` : ''}`);
}

/** A channel's ad records across months (newest first). */
export async function getDirectSoldAdsChannelHistory(channelId: string, from?: string): Promise<{ channelId: string; records: DsAdRecord[] }> {
  const qs = from ? `?from=${encodeURIComponent(from)}` : '';
  return adminRequest(`${DIRECT_SOLD_ADS_BASE}/channel/${encodeURIComponent(channelId)}${qs}`);
}

/** Kick off a detached historical backfill (admin). */
export async function startDirectSoldAdsBackfill(body?: { months?: string[]; channelKeys?: string[]; limit?: number }): Promise<{ status: string }> {
  return adminRequest(`${DIRECT_SOLD_ADS_BASE}/backfill`, {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
}

/** Kick off detached GAM campaign enrichment (admin). */
export async function startDirectSoldAdsEnrichment(body?: { month?: string; months?: string[]; force?: boolean }): Promise<{ status: string }> {
  return adminRequest(`${DIRECT_SOLD_ADS_BASE}/enrich`, {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
}

/**
 * Kick off a detached YouTube Analytics monthly capture/backfill (admin). This is the
 * YouTube side of Channel Intelligence — NOT the tracker-scrape ad backfill above.
 * Pass {from,to} for a range, {months} for an explicit set, or nothing for the latest
 * completed month. `includeAudience` also pulls demographics/geo/device/traffic.
 */
export async function startYoutubeAnalyticsBackfill(body?: {
  from?: string;
  to?: string;
  months?: string[];
  includeAudience?: boolean;
}): Promise<{ status: string; months?: string[] | string }> {
  return adminRequest(`${DIRECT_SOLD_ADS_BASE}/youtube/backfill`, {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
}

/** Status of a backfill/enrichment run. */
export async function getDirectSoldAdsRun(runId: string): Promise<{ run: any }> {
  return adminRequest(`${DIRECT_SOLD_ADS_BASE}/backfill/${encodeURIComponent(runId)}`);
}

/** Re-apply current channel display names (from the channel mapping) onto stored rollups. */
export async function refreshDirectSoldAdsChannelNames(): Promise<{
  monthsUpdated: number;
  changes: Array<{ channelId: string; from: string; to: string }>;
}> {
  return adminRequest(`${DIRECT_SOLD_ADS_BASE}/refresh-channel-names`, { method: 'POST' });
}
